/** @file The DescriptionPopover is a light tooltip wrapper over the Popover. */
import { flip, hide as hideMiddleware, offset, shift, } from '@floating-ui/dom';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Info } from '@codegrade/icons';
import { Icon } from '@/components/Icon';
import * as utils from '@/utils';
import { Popover } from './Popover';
/** DescriptionPopover is 'tooltip' styled wrapper over Popover.   */
export default tsx.componentFactoryOf().create({
    name: 'description-popover',
    props: {
        /** An alternative to using the slot to assign text. */
        description: p.ofType().default(undefined),
        /**
         * A concise, descriptive label for the popover's trigger button, and
         * link to documentation.  This text is read by screen readers to
         * announce the button's purpose, and inform what the documentation will
         * be about.
         *
         * @example "more information about submission settings"
         */
        ariaLabel: p(String).required,
        /** Used to set the placement. Will be overridden to flip or move if off screen. */
        placement: p.ofType().default('right'),
        /** The icon used. */
        icon: p.ofType().default(() => Info),
        /** Whether to render the icon as a superscript or not. */
        asSuperscript: p(Boolean).default(true),
        /** Title of the popover (this could possibly be removed). */
        title: p.ofType().default(undefined),
    },
    /** @returns The data for the component. */
    data() {
        return {
            id: `description-popover-${utils.getUniqueId()}`,
        };
    },
    computed: {
        /** @returns The attributes we want the underlyig popover to take. */
        popoverAttrs() {
            return {
                attrs: {
                    role: 'tooltip',
                    tabindex: -1,
                    'aria-live': 'polite',
                },
                class: ['cg-description-popover'],
            };
        },
        /** @returns The classes for the popover. */
        wrapperClasses() {
            const classes = ['description-popover', 'd-inline-block', 'cursor-help'];
            if (!this.asSuperscript) {
                classes.push('float-right');
            }
            return classes;
        },
        /** @returns The classes for the button itself. */
        buttonClasses() {
            return [
                'p-0 min-w-6 min-h-6',
                { 'flex items-center': !this.asSuperscript, 'text-sm': this.asSuperscript },
                'bg-white/0',
                'text-current hover:text-current/80',
                'border-0',
                'rounded',
                'cursor-help',
            ];
        },
        /** @returns The popover middleware which defines the positioning of the popover itself. */
        popoverMiddleware() {
            return [
                offset({ mainAxis: 4 }),
                flip({
                    padding: 16,
                    // This set to float overrides the reference in Popover and
                    // allows flipping when going off screen.
                    elementContext: 'floating',
                    fallbackPlacements: ['top', 'bottom', 'left', 'right'],
                }),
                shift({
                    padding: 16,
                    elementContext: 'floating',
                }),
                hideMiddleware({
                    padding: 16,
                    elementContext: 'reference',
                }),
            ];
        },
        /** @returns The optional title. */
        popoverTitle() {
            return utils.ifOrEmpty(this.title != null, () => (<div class="font-medium px-1 py-2 text-center bg-neutral-2 dark:bg-neutral-80" role="heading" aria-level="3">
                    {this.title}
                </div>));
        },
        /** @returns The main content of the popover. */
        popoverContent() {
            // Handles passing by prop or slot.
            const content = this.description ??
                (this.$scopedSlots.description?.() != null
                    ? this.$scopedSlots.description?.()
                    : this.$scopedSlots.default?.());
            // We apply the padding here to the content, which allows the title
            // to maintain full width.
            return (<div class="px-3 py-2.5 font-normal text-justify leading-relaxed" style={{ hyphens: 'auto' }}>
                    {content}
                </div>);
        },
    },
    methods: {
        /** @returns The popover content. */
        renderPopoverContent() {
            return (<div class="text-sm overflow-hidden">
                    {this.popoverTitle}
                    {this.popoverContent}
                </div>);
        },
        /**
         * Handles emitting when popover is opened.
         *
         * @param isOpen Whether to toggle is open or not.
         */
        handleToggle(isOpen) {
            tsx.emitOn(this, 'onToggle', isOpen);
        },
    },
    /** @returns The rendered Popover component. */
    render() {
        return (<Popover class={this.wrapperClasses} popoverAttrs={this.popoverAttrs} placement={this.placement} middleware={this.popoverMiddleware} onToggle={this.handleToggle} scopedSlots={{
                button: ({ hide, popoverId, toggle }) => (<button type="button" class={this.buttonClasses} aria-describedby={popoverId} aria-label={this.ariaLabel} onBlur={hide} popovertarget={popoverId} onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        toggle(event);
                    }}>
                            <Icon class={{
                        // align-super is the same as wrapping in <sup>
                        'align-super': this.asSuperscript,
                    }} icon={this.icon} aria-hidden="true"/>
                        </button>),
                popover: () => <div>{this.renderPopoverContent()}</div>,
            }}/>);
    },
});
